import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Instagram, ArrowRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { ASSETS, CONTACT } from "@/assets";

export default function PromoSection() {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/public/settings").then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const photo = settings.cart_photo_url ? mediaUrl(settings.cart_photo_url) : ASSETS.cartPhoto;
  const tagline =
    settings.tagline ||
    "Follow the sweetest cart in New York. Authentic Italian gelato, wherever Manhattan needs a little dolce vita.";
  const instagram = settings.instagram_url || CONTACT.instagram;

  return (
    <section id="promo" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="absolute -top-5 -left-5 w-28 h-28 rounded-full bg-sc-sage/30 -z-10" />
          <div className="absolute -bottom-6 -right-4 w-36 h-36 rounded-full bg-sc-gold/20 -z-10" />
          <img
            src={photo}
            alt="Santa Chiara gelato cart"
            className="w-full rounded-[2.5rem] object-cover shadow-[0_20px_60px_rgba(44,42,40,0.2)] border-4 border-white"
            style={{ aspectRatio: "3 / 4" }}
            data-testid="promo-cart-photo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="eyebrow mb-4">La dolce vita, on wheels</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight text-sc-espresso leading-tight">
            Follow the sweetest cart in <em className="italic font-light text-sc-gold">New York</em>.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-sc-espresso/80 leading-relaxed">
            {tagline}
          </p>
          <a
            href={instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-sc-espresso text-sc-cream font-medium hover:scale-105 transition-transform"
            data-testid="instagram-button"
          >
            <Instagram size={20} />
            {CONTACT.instagramHandle}
            <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-16 md:mt-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {ASSETS.gallery.map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_12px_40px_rgba(44,42,40,0.14)] group"
              data-testid={`gallery-image-${i}`}
            >
              <img
                src={src}
                alt={`Santa Chiara gelato cart ${i + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                style={{ aspectRatio: "3 / 4" }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
